// 画像アップロードをユーザーフレンドリーにするためのクライアント側前処理。
//
// 方針:
// - 対応形式かつ上限内の画像は無加工でそのまま返す（劣化させない）。
// - 上限超過のラスタ画像（PNG / JPEG / WebP / その他デコード可能な形式）は、ブラウザ上で縦横比を保って
//   縮小・再エンコードし、上限内に自動で収めてからアップロードする（利用者が手動で縮小しなくてよい）。
// - 非対応形式でもブラウザがデコードできれば対応形式（WebP / JPEG）へ自動変換する（例: BMP）。
// - SVG はベクターなのでラスタライズしない。GIF はアニメーション保持のためラスタライズしない。
//   これらが上限を超える場合のみ、原因の分かる文言で弾く（自動加工はしない）。
// - デコード不能（例: 多くのブラウザの HEIC）や、縮小しても上限に収まらない場合は、具体的な文言で弾く。

import {
  ALLOWED_IMAGE_MIME,
  IMAGE_FORMAT_LABEL,
  IMAGE_MAX_BYTES,
  formatMb,
  formatMbCeil,
} from "./upload-limits";

// 自動リサイズで許容する最長辺（px）。超解像度の写真を実用解像度へ抑える。
const DEFAULT_MAX_DIMENSION = 4096;
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.5;
const MAX_ENCODE_ATTEMPTS = 6;

export type PrepareResult =
  | { readonly ok: true; readonly file: File; readonly resized: boolean }
  | { readonly ok: false; readonly message: string };

/**
 * 縦横比を保ったまま最長辺を maxDim 以内へ収める寸法を返す（pure・テスト対象）。
 * maxDim 以内ならそのまま（拡大はしない）。0 以下の入力は最低 1px に丸める。
 */
export function computeScaledDimensions(
  width: number,
  height: number,
  maxDim: number
): { width: number; height: number } {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const longest = Math.max(w, h);
  if (longest <= maxDim) return { width: w, height: h };
  const scale = maxDim / longest;
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

function unsupportedImageMessage(type: string): string {
  const t = type || "形式不明";
  return `この画像（${t}）は対応形式ではなく、お使いのブラウザで変換もできませんでした。${IMAGE_FORMAT_LABEL} のいずれかに変換して再度お試しください。`;
}

function oversizedImageMessage(size: number, maxBytes: number): string {
  return `画像（${formatMbCeil(size)}）が上限の ${formatMb(maxBytes)} を超えています。画像を縮小して再度お試しください。`;
}

function renameWithExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^./\\]+$/, "");
  return `${base || "image"}.${ext}`;
}

/**
 * ラスタライズしてはいけない形式（SVG=ベクター / GIF=アニメーション）を判定する。
 * MIME（`file.type`）だけに頼らず拡張子もフォールバックで見る。理由: MIME が空や
 * `application/octet-stream` 等で渡る経路（将来の file input 等）でも、実体が SVG/GIF なら
 * 誤ってラスタライズして「ベクター性／アニメーションを失う」事故を避けるため。
 */
function detectVectorOrAnimated(file: File): "svg" | "gif" | null {
  const name = (file.name || "").toLowerCase();
  if (file.type === "image/svg+xml" || name.endsWith(".svg")) return "svg";
  if (file.type === "image/gif" || name.endsWith(".gif")) return "gif";
  return null;
}

/** バイト列中の ASCII マーカー（PNG/WebP のチャンク名等）の先頭インデックスを返す（無ければ -1）。 */
function indexOfMarker(bytes: Uint8Array, marker: string): number {
  for (let i = 0; i + marker.length <= bytes.length; i++) {
    let hit = true;
    for (let j = 0; j < marker.length; j++) {
      if (bytes[i + j] !== marker.charCodeAt(j)) {
        hit = false;
        break;
      }
    }
    if (hit) return i;
  }
  return -1;
}

/**
 * アニメーション PNG（APNG, `acTL` チャンク）/ アニメーション WebP（`ANIM` チャンク）を、先頭領域の
 * チャンクマーカーで検出する。これらは MIME が image/png / image/webp（対応形式）だが、canvas で
 * 再エンコードするとアニメーションを失うため、GIF と同様にラスタライズ対象から外す。
 * 誤検知を避けるため、単純な全域文字列一致ではなくチャンク構造の前提で絞り込む:
 * - APNG: `acTL` は仕様上 `IDAT` より前に現れる。`IDAT` より前に `acTL` がある場合のみアニメ扱い
 *   （`IDAT` 以降の圧縮ピクセルデータ中で偶然 4 文字が一致するケースを除外）。
 * - animated WebP: `ANIM` チャンクは `RIFF`/`WEBP`/`VP8X` ヘッダ直後（先頭付近）に現れるため、
 *   先頭 256 バイト以内に出現した場合のみアニメ扱い（後続データ中の偶然一致を除外）。
 */
async function isAnimatedRaster(file: File): Promise<boolean> {
  const name = (file.name || "").toLowerCase();
  const maybePng = file.type === "image/png" || name.endsWith(".png");
  const maybeWebp = file.type === "image/webp" || name.endsWith(".webp");
  if (!maybePng && !maybeWebp) return false;
  try {
    const head = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
    if (maybePng) {
      const acTL = indexOfMarker(head, "acTL");
      const idat = indexOfMarker(head, "IDAT");
      if (acTL >= 0 && (idat < 0 || acTL < idat)) return true;
    }
    if (maybeWebp) {
      const anim = indexOfMarker(head, "ANIM");
      if (anim >= 0 && anim < 256) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// canvas / 画像デコード API が使える環境か（jsdom 等のテスト環境・SSR では使えない）。
function canRasterize(): boolean {
  return (
    typeof createImageBitmap === "function" &&
    (typeof OffscreenCanvas === "function" || typeof document !== "undefined")
  );
}

async function encodeBitmap(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  type: string,
  quality: number
): Promise<Blob | null> {
  // OffscreenCanvas を優先するが、「存在はするが 2D 描画/Blob 化が使えない」環境では DOM canvas へ
  // 明示フォールバックする（getContext が null／convertToBlob 失敗でアップロード不能にしない）。
  if (typeof OffscreenCanvas === "function") {
    try {
      const offscreen = new OffscreenCanvas(width, height);
      const ctx = offscreen.getContext("2d");
      if (ctx) {
        ctx.drawImage(bitmap, 0, 0, width, height);
        const blob = await offscreen.convertToBlob({ type, quality });
        if (blob) return blob;
      }
    } catch {
      // OffscreenCanvas が unusable → 下の DOM canvas フォールバックへ。
    }
  }
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, width, height);
  return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), type, quality));
}

/**
 * 画像をアップロード前に整える。
 * - 対応形式かつ上限内 → 無加工で返す（resized=false）。
 * - 上限超過 or 非対応（でもデコード可能） → 縮小・再エンコードして上限内に収めて返す（resized=true）。
 * - SVG / GIF は加工せず、上限超過時のみ具体的な文言で弾く。
 * - デコード不能・縮小しても収まらない → 具体的な文言で弾く（ok:false）。
 */
export async function prepareImageForUpload(
  file: File,
  options?: { maxBytes?: number | undefined; maxDimension?: number | undefined }
): Promise<PrepareResult> {
  const maxBytes = options?.maxBytes ?? IMAGE_MAX_BYTES;
  const maxDim = options?.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const type = file.type;

  // 0 バイト（空ファイル）はバックエンドの contentLength: positive() で 400 になるため、
  // 原因の分かる文言で先に弾く（SVG/GIF を含む全形式に先行して判定）。
  if (file.size <= 0) {
    return { ok: false, message: "画像ファイルが空です（0バイト）。別のファイルを選択してください。" };
  }

  // SVG/GIF はラスタライズしない（MIME だけでなく拡張子もフォールバックで判定）。
  // 拡張子で判定したが MIME が空/不一致の場合は、後段の presign（contentType）や readSvgSize が
  // file.type を見るため、正しい MIME を付けた File へ正規化して end-to-end の整合を保つ。
  const vectorOrAnimated = detectVectorOrAnimated(file);
  if (vectorOrAnimated === "svg") {
    if (file.size > maxBytes) {
      return {
        ok: false,
        message: `SVG 画像（${formatMbCeil(file.size)}）が上限の ${formatMb(maxBytes)} を超えています。ファイルを軽量化して再度お試しください。`,
      };
    }
    const normalized = file.type === "image/svg+xml" ? file : new File([file], file.name, { type: "image/svg+xml" });
    return { ok: true, file: normalized, resized: false };
  }
  if (vectorOrAnimated === "gif") {
    if (file.size > maxBytes) {
      return {
        ok: false,
        message: `GIF 画像（${formatMbCeil(file.size)}）が上限の ${formatMb(maxBytes)} を超えています。アニメーション保持のため自動リサイズはできません。ファイルを軽量化するか静止画として書き出して再度お試しください。`,
      };
    }
    const normalized = file.type === "image/gif" ? file : new File([file], file.name, { type: "image/gif" });
    return { ok: true, file: normalized, resized: false };
  }

  const isSupported = (ALLOWED_IMAGE_MIME as readonly string[]).includes(type);
  // 対応形式かつ上限内 → 無加工（品質を落とさない）。
  if (isSupported && file.size <= maxBytes) return { ok: true, file, resized: false };

  // アニメーション PNG(APNG)/WebP は canvas 再エンコードでアニメーションを失うため、GIF と同様に
  // 自動加工しない（ここに来る＝上限超過。アニメーション保持のため縮小せず文言で案内する）。
  if (await isAnimatedRaster(file)) {
    return {
      ok: false,
      message: `アニメーション画像（${formatMb(file.size)}）が上限の ${formatMb(maxBytes)} を超えています。アニメーション保持のため自動リサイズはできません。ファイルを軽量化して再度お試しください。`,
    };
  }

  // ここに来るのは「上限超過のラスタ画像（非アニメーション）」または「非対応形式（変換を試みる）」。
  if (!canRasterize()) {
    // 加工不能な環境。非対応なら非対応メッセージ、上限超過なら縮小依頼メッセージ。
    return {
      ok: false,
      message: isSupported ? oversizedImageMessage(file.size, maxBytes) : unsupportedImageMessage(type),
    };
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // デコード不能（例: 多くのブラウザの HEIC）。
    return {
      ok: false,
      message: isSupported ? oversizedImageMessage(file.size, maxBytes) : unsupportedImageMessage(type),
    };
  }

  try {
    // JPEG ソース（透過なし）だけ JPEG で再エンコードし、それ以外（PNG / WebP / BMP / 不明等、
    // 透過を持ち得る形式）は WebP（透過対応＆高圧縮）で再エンコードする。これにより、デコード可能な
    // 非対応のアルファ付き形式を JPEG 化して透明部分を失う事故を防ぐ。WebP 非対応ブラウザでは下流で
    // PNG へフォールバックし、実際の Blob.type を MIME の正本にするため透過は保たれる。
    const encodeType = type === "image/jpeg" ? "image/jpeg" : "image/webp";
    let currentMaxDim = maxDim;
    let quality = INITIAL_QUALITY;
    let best: Blob | null = null;

    for (let attempt = 0; attempt < MAX_ENCODE_ATTEMPTS; attempt++) {
      const { width, height } = computeScaledDimensions(bitmap.width, bitmap.height, currentMaxDim);
      const blob = await encodeBitmap(bitmap, width, height, encodeType, quality);
      if (!blob) break;
      best = blob;
      if (blob.size <= maxBytes) break;
      // まだ大きい → 寸法を縮め、品質も少し下げて再試行。
      currentMaxDim = Math.max(256, Math.round(currentMaxDim * 0.8));
      quality = Math.max(MIN_QUALITY, quality - 0.1);
    }

    if (!best) {
      return {
        ok: false,
        message: isSupported ? oversizedImageMessage(file.size, maxBytes) : unsupportedImageMessage(type),
      };
    }
    if (best.size > maxBytes) {
      return {
        ok: false,
        message: `画像を自動的に縮小しましたが、上限の ${formatMb(maxBytes)} 以内に収められませんでした（${formatMbCeil(best.size)}）。より小さい画像で再度お試しください。`,
      };
    }
    // canvas が要求形式（WebP 等）をサポートせず別形式（多くは PNG）へフォールバックすることがあるため、
    // 要求 encodeType を盲信せず、実際に得られた Blob.type を MIME の正本にする。これをしないと
    // S3 の Content-Type と実バイト列が食い違い、表示・デコードに失敗する。
    const actualType = best.type;
    if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(actualType)) {
      return {
        ok: false,
        message: `お使いのブラウザで画像を対応形式へ変換できませんでした。${IMAGE_FORMAT_LABEL} のいずれかに変換して再度お試しください。`,
      };
    }
    const ext = actualType === "image/webp" ? "webp" : actualType === "image/png" ? "png" : "jpg";
    const newFile = new File([best], renameWithExtension(file.name, ext), { type: actualType });
    return { ok: true, file: newFile, resized: true };
  } catch {
    return {
      ok: false,
      message: isSupported ? oversizedImageMessage(file.size, maxBytes) : unsupportedImageMessage(type),
    };
  } finally {
    // 例外経路も含め ImageBitmap を必ず解放する（連続処理でのメモリ/GPU リソース保持を防ぐ）。
    if (typeof bitmap.close === "function") bitmap.close();
  }
}
